import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { Calendar, Clock, Users, ArrowLeft, Video, CheckCircle } from 'lucide-react';
import { useGeoCurrency } from '@/hooks/useGeoCurrency';

function JitsiEmbed({ roomName, userName }) {
  const domain = 'meet.jit.si';
  const src = `https://${domain}/${roomName}#config.prejoinConfig.enabled=false&config.startWithAudioMuted=true&config.startWithVideoMuted=false&userInfo.displayName="${encodeURIComponent(userName)}"`;

  return (
    <div className="aspect-video rounded-xl overflow-hidden border border-sacred-gold/10" data-testid="jitsi-embed">
      <iframe
        src={src}
        className="w-full h-full"
        allow="camera; microphone; fullscreen; display-capture; autoplay"
        allowFullScreen
        title="Live Event"
        style={{ border: 0 }}
      />
    </div>
  );
}

export default function EventDetail() {
  const { slug } = useParams();
  const { t, localizedField } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency } = useGeoCurrency();
  const [event, setEvent] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: e } = await api.get(`/events/${slug}`);
        setEvent(e);
        if (user) {
          try {
            const { data: r } = await api.get(`/events/${slug}/registration`);
            if (r) setRegistration(r);
          } catch {}
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [slug, user]);

  const handleRegister = async () => {
    if (!user) { window.location.href = '/auth'; return; }
    setRegistering(true);
    try {
      const { data } = await api.post(`/events/${slug}/register`);
      setRegistration(data);
    } catch {}
    setRegistering(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-text-muted font-body">{t('common.loading')}</div>;
  if (!event) return <div className="min-h-screen flex items-center justify-center text-text-muted font-body">{t('common.notFound')}</div>;

  const isLive = event.status === 'live';
  const isUpcoming = event.status === 'upcoming';
  const isEnded = event.status === 'ended';
  const canJoin = isLive && registration;

  return (
    <div data-testid="event-detail-page" className="min-h-screen pb-20">
      <div className="px-4 pt-6 pb-4 max-w-5xl mx-auto">
        <Link to="/events" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-heading font-body transition-colors" data-testid="back-to-events">
          <ArrowLeft size={16} /> {t('events.title')}
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-4">
        {canJoin && (
          <ScrollReveal>
            <div className="mb-8">
              <JitsiEmbed roomName={event.jitsi_room_name} userName={user?.display_name || user?.username || 'Attendee'} />
            </div>
          </ScrollReveal>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <ScrollReveal>
              <div className="mb-4 flex items-center gap-3">
                {isLive && <span className="px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-sm font-body animate-pulse">LIVE NOW</span>}
                {isUpcoming && <span className="px-3 py-1 rounded-full bg-sacred-gold/10 text-sacred-gold text-sm font-body">Upcoming</span>}
                {isEnded && <span className="px-3 py-1 rounded-full bg-dust-gray/10 text-dust-gray text-sm font-body">Ended</span>}
                {event.is_free && <span className="px-3 py-1 rounded-full bg-accent-success/10 text-accent-success text-sm font-body">Free</span>}
              </div>

              <h1 className="text-3xl sm:text-4xl font-display text-heading mb-4" data-testid="event-title">{localizedField(event.title)}</h1>
              <p className="text-text-muted font-body leading-relaxed">{localizedField(event.description)}</p>
            </ScrollReveal>

            {!canJoin && isLive && !registration && (
              <ScrollReveal delay={0.1}>
                <div className="mt-8 glass-card rounded-xl p-6 border-l-4 border-sacred-gold">
                  <div className="flex items-center gap-3 mb-3">
                    <Video size={20} className="text-sacred-gold" />
                    <h3 className="font-display text-heading">This event is live now!</h3>
                  </div>
                  <p className="text-text-muted font-body text-sm">Register to join the live session.</p>
                </div>
              </ScrollReveal>
            )}

            {canJoin && (
              <ScrollReveal delay={0.1}>
                <div className="mt-6 glass-card rounded-xl p-4 border-l-4 border-accent-success">
                  <div className="flex items-center gap-2 text-accent-success font-body text-sm">
                    <CheckCircle size={16} /> You are connected to the live session above.
                  </div>
                </div>
              </ScrollReveal>
            )}
          </div>

          <div className="md:col-span-1">
            <ScrollReveal>
              <div className="glass-card rounded-xl p-5 sticky top-24" data-testid="event-sidebar">
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 text-sm font-body">
                    <Calendar size={16} className="text-sacred-gold shrink-0" />
                    <div>
                      <p className="text-heading">{formatDate(event.event_date)}</p>
                      <p className="text-text-muted text-xs">{formatTime(event.event_date)}</p>
                    </div>
                  </div>
                  {event.duration_minutes && (
                    <div className="flex items-center gap-3 text-sm font-body">
                      <Clock size={16} className="text-sacred-gold shrink-0" />
                      <p className="text-heading">{event.duration_minutes} minutes</p>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm font-body">
                    <Users size={16} className="text-sacred-gold shrink-0" />
                    <p className="text-heading">{event.registered_count || 0}{event.max_attendees ? ` / ${event.max_attendees} seats` : ' registered'}</p>
                  </div>
                  <div className="flex items-center gap-3 text-sm font-body">
                    <Video size={16} className="text-sacred-gold shrink-0" />
                    <p className="text-heading">Live via Jitsi Meet</p>
                  </div>
                </div>

                {!event.is_free && (
                  <div className="text-center mb-4">
                    <span className="text-2xl font-display text-heading">{currency === 'ILS' && event.price_ils ? `\u20AA${event.price_ils}` : `$${event.price_usd}`}</span>
                  </div>
                )}

                {registration ? (
                  <div className="text-center py-3 rounded-full bg-accent-success/10 text-accent-success font-body text-sm" data-testid="registered-badge">
                    <CheckCircle size={16} className="inline mr-2" /> Registered
                  </div>
                ) : isEnded ? (
                  <div className="text-center py-3 rounded-full bg-dust-gray/10 text-dust-gray font-body text-sm">Event has ended</div>
                ) : (
                  <button
                    onClick={() => {
                      // Paid items go straight to checkout (guest checkout supported);
                      // only free self-enrollment needs an account
                      if (!event.is_free) {
                        navigate(`/checkout?type=event&id=${event.id}&currency=${currency}`);
                        return;
                      }
                      if (!user) { navigate('/auth', { state: { from: window.location.pathname } }); return; }
                      handleRegister();
                    }}
                    disabled={registering}
                    className="w-full gold-button py-3 rounded-full font-body text-sm font-semibold"
                    data-testid="register-btn"
                  >
                    {registering ? 'Registering...' : event.is_free ? t('events.register') : `Buy Ticket — ${currency === 'ILS' && event.price_ils ? `\u20AA${event.price_ils}` : `$${event.price_usd}`}`}
                  </button>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </div>
  );
}
